//tray-form.tsx

import React from "react";
import type { JSX } from "react";
import { Formik, Form, FastField, FieldProps, FormikHelpers } from "formik";
import * as yup from "yup";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  IconButton,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { AppTextField } from "./text-field";
import { validationMessages } from "../utils/validation/messages";
import { FormPrompt } from "./form-prompt";
import { unitsApi } from "../services/api/units";
import DeleteIcon from "@mui/icons-material/Delete";
import { DateTime } from "luxon";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

export interface TrayFormValues {
  id: number;
  name: string;
  soilPerTray: string;
  unit: string;
  notes: string;
  archived: boolean;
}

export interface TrayFormValues {
  id: number;
  name: string;
  soilPerTray: string;
  unit: string;
  notes: string;
}

export default function TrayForm(props: {
  children: JSX.Element;
  initialValues: TrayFormValues;
  saved: boolean;
  unitOptions: {
    id: number;
    name: string;
    archivedAt: string | null;
  }[];
  onUnitsChanged?: () => void;
  onSubmit: (
    values: TrayFormValues,
    helpers: FormikHelpers<TrayFormValues>,
  ) => void;
}) {
  const [customUnit, setCustomUnit] = React.useState("");
  const [unitDialogOpen, setUnitDialogOpen] = React.useState(false);

  return (
    <Formik
      initialValues={props.initialValues}
      validationSchema={yup.object({
        name: yup.string().required(validationMessages.Required),
        soilPerTray: yup.string(),
        unit: yup.string().required(validationMessages.Required),
        notes: yup.string().max(5000),
        archived: yup.boolean(),
      })}
      onSubmit={async (values, formikHelpers) => {
        await props.onSubmit(values, formikHelpers);
        formikHelpers.resetForm({ values });
      }}
    >
      {(form) => (
        <>
          <FormPrompt form={form} saved={props.saved} />
          <Form noValidate>
            <Box>
              <FormControl
                variant="standard"
                error={form.touched.archived && Boolean(form.errors.archived)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="archived"
                      color="primary"
                      onChange={form.handleChange}
                      checked={form.values.archived}
                    />
                  }
                  label="Archived"
                />
                {form.touched.archived && form.errors.archived && (
                  <FormHelperText>{form.errors.archived}</FormHelperText>
                )}
              </FormControl>
            </Box>
            <FastField name="name">
              {(fieldProps: FieldProps) => (
                <AppTextField {...fieldProps} autoFocus label="Name" />
              )}
            </FastField>
            <FastField name="soilPerTray">
              {(fieldProps: FieldProps) => (
                <AppTextField {...fieldProps} label="Soil Per Tray" />
              )}
            </FastField>
            <FastField name="unit">
              {(fieldProps: FieldProps) => (
                <AppTextField
                  {...fieldProps}
                  select
                  label="Unit"
                  field={{
                    ...fieldProps.field,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.value === "__manage_units__") {
                        setCustomUnit("");
                        setUnitDialogOpen(true);
                        return;
                      }

                      fieldProps.field.onChange(e);
                    },
                  }}
                >
                  {props.unitOptions.map((option) => (
                    <MenuItem key={option.id} value={option.name}>
                      {option.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="__manage_units__">
                    + Add / Edit Units
                  </MenuItem>
                </AppTextField>
              )}
            </FastField>
            <FastField name="notes">
              {(fieldProps: FieldProps) => (
                <AppTextField
                  {...fieldProps}
                  multiline
                  label="Notes"
                  rows={4}
                />
              )}
            </FastField>
            <Dialog
              open={unitDialogOpen}
              onClose={() => setUnitDialogOpen(false)}
            >
              <DialogTitle>Add / Edit Units</DialogTitle>
              <DialogContent>
                {props.unitOptions.map((unit) => (
                  <Box
                    key={unit.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <span>{unit.name}</span>
                    <IconButton
                      size="small"
                      aria-label="archive unit"
                      onClick={() => {
                        unitsApi
                          .update({
                            ...unit,
                            archivedAt: DateTime.now().toISO(),
                          })
                          .then(async () => {
                            await props.onUnitsChanged?.();
                          });
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <TextField
                  label="Unit"
                  value={customUnit}
                  onChange={(e) => setCustomUnit(e.target.value)}
                  autoFocus
                  sx={{ mt: 1 }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setUnitDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    const nextUnit = customUnit.trim();

                    if (!nextUnit) {
                      return;
                    }
                    unitsApi
                      .add({
                        id: 0,
                        name: nextUnit,
                        archivedAt: null,
                      })
                      .then(() => {
                        form.setFieldValue("unit", nextUnit);
                        setUnitDialogOpen(false);
                      });
                  }}
                >
                  Add
                </Button>
              </DialogActions>
            </Dialog>
            <Box>{props.children}</Box>
          </Form>
        </>
      )}
    </Formik>
  );
}
